import {
  AssetCoverageSummary,
  AssetDto,
  SaasAssetActiveStatus,
  SaasAssetAttributionReason,
  SaasAssetAttributionStatus,
  SaasAssetDto,
  SaasUserDto,
  UserCoverageReport,
  UserCoverageSummary,
} from '@ampsec/platform-client';
import _ from 'lodash';

function isLocalHostname(value: string | undefined): boolean {
  return !!value && value.trim().toLowerCase().endsWith('.local');
}

function getSaasAssetHostnames(saasAsset: SaasAssetDto): string[] {
  const hostNames = saasAsset.meta?._asset?.hostNames;
  return Array.isArray(hostNames) ? hostNames.filter((hn) => !!hn) : [];
}

function normalizeGroupedRecord<T>(record: Record<string, T | T[]> | undefined): Record<string, T[]> {
  if (!record) {
    return {};
  }
  return Object.fromEntries(
    Object.entries(record).map(([key, value]) => [key, Array.isArray(value) ? value : value ? [value] : []]),
  );
}

function getAssetInfo(
  asset: AssetDto,
  saasAssets: SaasAssetDto[],
): {
  sn?: string;
  os?: string;
  hostname?: string;
  model?: string;
  lastActivity?: string;
  isAssetActive: boolean;
} {
  const saasValues = saasAssets.reduce(
    (result, sa) => {
      const meta = sa.meta?._asset;
      result.sn = result.sn || meta?.sn || asset.extKeys?.sn;
      result.os = result.os || meta?.operatingSystems?.find((os) => os.displayValue)?.displayValue;
      result.model = result.model || meta?.model;
      if (result.lastActivity && meta?.lastActivityTime) {
        result.lastActivity =
          result.lastActivity > meta.lastActivityTime ? result.lastActivity : meta.lastActivityTime;
      } else if (meta?.lastActivityTime) {
        result.lastActivity = meta.lastActivityTime;
      }
      return result;
    },
    {} as Record<string, string | undefined>,
  );

  // Prefer a `.local` host name when the device is known by more than one name.
  const hostNameCandidates = saasAssets.flatMap((sa) => getSaasAssetHostnames(sa));
  const hostname = hostNameCandidates.find((hn) => isLocalHostname(hn)) ?? hostNameCandidates[0];

  const isAssetActive = !saasAssets?.every(
    (sa) =>
      sa.meta?._asset?.activeStatus === SaasAssetActiveStatus.ARCHIVED ||
      sa.meta?._asset?.activeStatus === SaasAssetActiveStatus.INACTIVE,
  );

  return {
    sn: asset.extKeys?.sn || saasValues.sn,
    os: saasValues.os,
    hostname,
    model: saasValues.model,
    lastActivity: saasValues.lastActivity || asset.lastActivityTime,
    isAssetActive: saasAssets.length > 0 ? isAssetActive : true,
  };
}

function getAssetAttributionInfo(saasAssets: SaasAssetDto[]): {
  status?: AssetCoverageSummary['attributionStatus'];
  reason?: AssetCoverageSummary['attributionReason'];
} {
  const attributions = saasAssets
    .map((saasAsset) => saasAsset.meta?._asset?.attribution)
    .filter(Boolean);
  const negative = attributions.find((attribution) => attribution?.status === SaasAssetAttributionStatus.NEGATIVE);
  if (negative) {
    return {
      status: SaasAssetAttributionStatus.NEGATIVE,
      reason: negative.reason ?? SaasAssetAttributionReason.OTHER,
    };
  }
  const positive = attributions.find((attribution) => attribution?.status === SaasAssetAttributionStatus.POSITIVE);
  if (positive) {
    return { status: SaasAssetAttributionStatus.POSITIVE };
  }
  return {};
}

function buildAssetAccounts2(
  saasAssets: SaasAssetDto[],
  connectorIds: string[],
): AssetCoverageSummary['assetAccounts2'] {
  const saasAssetByCid = _.groupBy(saasAssets, 'cid');
  const assetAccounts2: AssetCoverageSummary['assetAccounts2'] = {};

  connectorIds.forEach((cid) => {
    const saasAssetsInCid = saasAssetByCid[cid] ?? [];
    if (saasAssetsInCid.length === 0) {
      assetAccounts2[cid] = [];
      return;
    }

    const active = saasAssetsInCid.some((saasAsset) => {
      const assetMeta = saasAsset.meta?._asset;
      const explicitlyActive = assetMeta?.active === true;
      const notInactiveStatus = assetMeta?.activeStatus !== SaasAssetActiveStatus.INACTIVE;
      const notArchivedStatus = assetMeta?.activeStatus !== SaasAssetActiveStatus.ARCHIVED;
      return explicitlyActive || (notInactiveStatus && notArchivedStatus);
    });

    const lastActivity = saasAssetsInCid
      .map((saasAsset) => saasAsset.meta?._asset?.lastActivityTime)
      .filter((activityTime): activityTime is string => _.isString(activityTime))
      .sort()
      .reverse()[0];

    assetAccounts2[cid] = [{ active, lastActivity }] as unknown as AssetCoverageSummary['assetAccounts2'][string];
  });

  return assetAccounts2;
}

function buildUserAccounts2(
  userAccounts: Record<string, SaasUserDto[]>,
): UserCoverageSummary['userAccounts2'] {
  return Object.fromEntries(
    Object.entries(userAccounts).map(([cid, users]) => [
      cid,
      users.map((user) => ({
        active: true,
        lastActivity: user.meta?._user?.lastActivityTime,
      })),
    ]),
  );
}

function toAssetCoverageSummary(
  asset: AssetDto,
  saasAssets: SaasAssetDto[],
  connectorIds: string[],
): AssetCoverageSummary {
  const assetInfo = getAssetInfo(asset, saasAssets);
  const attributionInfo = getAssetAttributionInfo(saasAssets);

  return {
    id: asset.id,
    uid: asset.uid,
    displayValue: asset.displayValue,
    sn: assetInfo.sn,
    hostname: assetInfo.hostname,
    model: assetInfo.model,
    os: assetInfo.os,
    lastCheckinTs: assetInfo.lastActivity,
    isAssetActive: assetInfo.isAssetActive,
    attributionStatus: attributionInfo.status,
    attributionReason: attributionInfo.reason,
    saasAssets: saasAssets.map((saasAsset) => ({
      id: saasAsset.id,
      cid: saasAsset.cid,
      displayValue: saasAsset.displayValue,
      uid: saasAsset.uid,
      ownerEmail: saasAsset.meta?._asset?.owner?.email,
      attributionStatus: saasAsset.meta?._asset?.attribution?.status,
      attributionReason: saasAsset.meta?._asset?.attribution?.reason,
    })),
    assetAccounts: {},
    assetAccounts2: buildAssetAccounts2(saasAssets, connectorIds),
  };
}

export function getSaasAssetFromUserCoverageReport(
  report: UserCoverageReport | Record<string, never> | null | undefined,
  aid: string,
  cid: string,
): SaasAssetDto | null {
  if (!report?.id || !aid || !cid) {
    return null;
  }

  const normalizedAssetAccounts = normalizeGroupedRecord<SaasAssetDto>(
    (report as UserCoverageReport).assetAccounts,
  );
  const saasAssetsForConnector = normalizedAssetAccounts[cid] ?? [];

  // Prefer the SaaS asset that belongs to this exact asset id.
  const exact = saasAssetsForConnector.find((saasAsset) => saasAsset.aid === aid);
  if (exact) {
    return exact;
  }

  const assets = Array.isArray(report.assets) ? report.assets : [];
  const targetAsset = assets.find((asset) => asset.id === aid);
  if (!targetAsset) {
    return null;
  }

  // The same device can be reported under more than one asset id, so when this
  // asset id has no SaaS coverage in this connector, fall back to the coverage
  // of a same-serial sibling — the same grouping buildUserAssetCoverageRows uses.
  const allSaasAssets = Object.values(normalizedAssetAccounts).flat();
  const saasAssetsForAsset = (asset: AssetDto): SaasAssetDto[] =>
    allSaasAssets.filter((saasAsset) => saasAsset.aid === (asset.id ?? ''));
  const targetSn = resolveSerialNumberKey(targetAsset, saasAssetsForAsset(targetAsset));
  if (!targetSn) {
    return null;
  }
  const sameSerialAids = assets
    .filter(
      (asset) =>
        (asset.id ?? '') !== aid && resolveSerialNumberKey(asset, saasAssetsForAsset(asset)) === targetSn,
    )
    .map((asset) => asset.id ?? '');

  return saasAssetsForConnector.find((saasAsset) => sameSerialAids.includes(saasAsset.aid)) ?? null;
}

function hasLocalName(asset: AssetDto): boolean {
  return isLocalHostname(asset.displayValue) || isLocalHostname(asset.extKeys?.hostname);
}

/**
 * Resolve the serial number that identifies a device. Prefer the asset's own
 * ext-key serial number; when that is missing, fall back to the serial number
 * carried on its SaaS coverage so duplicates that only share a SaaS-side serial
 * number still collapse.
 */
function resolveSerialNumberKey(asset: AssetDto, saasAssets: SaasAssetDto[]): string {
  const fromExtKeys = (asset.extKeys?.sn ?? '').trim().toLowerCase();
  if (fromExtKeys) {
    return fromExtKeys;
  }
  const fromSaas = saasAssets
    .map((sa) => (sa.meta?._asset?.sn ?? '').trim().toLowerCase())
    .find((sn) => sn);
  return fromSaas ?? '';
}

/**
 * Assets are unique by serial number, so two assets sharing an sn are the same
 * device and must not both be displayed. Group same-serial assets together,
 * matching on the ext-key serial number (or the SaaS serial number when the ext
 * key is missing). Assets without a serial number can't be grouped and each
 * stays its own row. Original order is preserved.
 */
function groupAssetsBySerialNumber(
  assets: AssetDto[],
  saasAssetsForAsset: (asset: AssetDto) => SaasAssetDto[],
): AssetDto[][] {
  const groups: AssetDto[][] = [];
  const groupIndexBySn = new Map<string, number>();

  assets.forEach((asset) => {
    const sn = resolveSerialNumberKey(asset, saasAssetsForAsset(asset));
    if (!sn) {
      groups.push([asset]);
      return;
    }
    const existingIndex = groupIndexBySn.get(sn);
    if (existingIndex === undefined) {
      groupIndexBySn.set(sn, groups.length);
      groups.push([asset]);
      return;
    }
    groups[existingIndex].push(asset);
  });

  return groups;
}

export function buildUserAssetCoverageRows(report: UserCoverageReport | Record<string, never>): UserCoverageSummary[] {
  if (!report?.id || !Array.isArray(report.assets)) {
    return [];
  }

  const normalizedAssetAccounts = normalizeGroupedRecord<SaasAssetDto>(report.assetAccounts);
  const normalizedUserAccounts = normalizeGroupedRecord<SaasUserDto>(report.userAccounts);
  const allSaasAssets = Object.values(normalizedAssetAccounts).flat();
  const saasAssetsByAid = _.groupBy(allSaasAssets, 'aid');
  const saasAssetsForAsset = (asset: AssetDto): SaasAssetDto[] => saasAssetsByAid[asset.id ?? ''] ?? [];
  const connectorIds = _.uniq([
    ...Object.keys(normalizedAssetAccounts),
    ...Object.keys(normalizedUserAccounts),
  ]);
  const userAccounts2 = buildUserAccounts2(normalizedUserAccounts);
  const userName = [report.firstName, report.lastName].filter(Boolean).join(' ').trim();
  const assetGroups = groupAssetsBySerialNumber(report.assets, saasAssetsForAsset);

  return assetGroups.map((group) => {
    // Prefer the asset whose own name ends in `.local` when the device is known
    // by more than one name.
    const primaryAsset = group.find(hasLocalName) ?? group[0];
    // The same device can be reported under more than one asset id, so combine
    // the SaaS coverage of every same-serial asset before computing the row.
    const saasAssets = group.flatMap((asset) => saasAssetsForAsset(asset));
    const assetCoverage = toAssetCoverageSummary(primaryAsset, saasAssets, connectorIds);

    return {
      id: primaryAsset.id ?? '',
      name: userName,
      firstName: report.firstName,
      lastName: report.lastName,
      email: report.email,
      department: report.department ?? '',
      organization: report.organization ?? '',
      userAccounts2,
      assets: [assetCoverage],
    } as UserCoverageSummary;
  });
}
